/**
 * Product Analytics Service - Stub Implementation
 * 
 * This is a placeholder service that logs analytics to console.
 * To enable production analytics with PostHog:
 * 
 * 1. Install: npm install posthog-js
 * 2. Create account at https://posthog.com (free tier available)
 * 3. Create a project
 * 4. Add VITE_POSTHOG_KEY and VITE_POSTHOG_HOST to your .env file
 * 
 * For now, all events are logged to console in development.
 */

let isInitialized = false;

// Event names - centralized for consistency
export const ANALYTICS_EVENTS = {
  // Auth Events
  SIGNUP_STARTED: 'signup_started',
  SIGNUP_COMPLETED: 'signup_completed',
  LOGIN_COMPLETED: 'login_completed',
  LOGOUT: 'logout',
  
  // Onboarding Events
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_SKIPPED: 'onboarding_skipped',
  
  // Core Feature Events
  TASK_CREATED: 'task_created',
  TASK_COMPLETED: 'task_completed',
  FOCUS_SESSION_STARTED: 'focus_session_started',
  FOCUS_SESSION_COMPLETED: 'focus_session_completed',
  AI_TIPS_REQUESTED: 'ai_tips_requested',
  SMART_TASK_REQUESTED: 'smart_task_requested',
  
  // Engagement Events
  DAILY_CHECKIN_COMPLETED: 'daily_checkin_completed',
  STREAK_ACHIEVED: 'streak_achieved',
  LEVEL_UP: 'level_up',
  QUEST_COMPLETED: 'quest_completed',
  
  // Social Events
  GROUP_CREATED: 'group_created',
  GROUP_JOINED: 'group_joined',
  
  // Premium Events
  UPGRADE_MODAL_OPENED: 'upgrade_modal_opened',
  CHECKOUT_STARTED: 'checkout_started',
  SUBSCRIPTION_ACTIVATED: 'subscription_activated',
  
  // Navigation Events
  PAGE_VIEWED: 'page_viewed',
  FEATURE_CLICKED: 'feature_clicked',
} as const;

type AnalyticsEvent = typeof ANALYTICS_EVENTS[keyof typeof ANALYTICS_EVENTS];

interface UserProperties {
  userId?: string;
  email?: string;
  name?: string;
  isPremium?: boolean;
  signupDate?: string;
  [key: string]: unknown;
}

/**
 * Initialize analytics
 * Currently logs setup instructions - implement PostHog when ready
 */
export async function initAnalytics(): Promise<void> {
  // Skip in development unless explicitly enabled
  if (import.meta.env.DEV && !import.meta.env.VITE_ANALYTICS_DEV) {
    console.log('[Analytics] Analytics disabled in development');
    return;
  }

  const apiKey = import.meta.env.VITE_POSTHOG_KEY;

  if (apiKey) {
    console.log(
      '[Analytics] VITE_POSTHOG_KEY is configured but PostHog is not installed.\n' +
      'To enable: npm install posthog-js, then update this service.'
    );
  } else if (!import.meta.env.DEV) {
    console.log('[Analytics] For production analytics, set up PostHog.');
  }

  isInitialized = true;
}

/**
 * Identify user (call on login/signup)
 * Stub: logs to console
 */
export function identifyUser(properties: UserProperties): void {
  if (!isInitialized) return;
  console.log('[Analytics] User identified:', properties.userId);
}

/**
 * Reset user identity (call on logout)
 */
export function resetAnalyticsUser(): void {
  if (!isInitialized) return;
  console.log('[Analytics] User reset');
}

/**
 * Track an event
 * Stub: logs to console in development
 */
export function trackEvent(
  event: AnalyticsEvent | string,
  properties?: Record<string, unknown>
): void {
  // Log to console in development
  if (import.meta.env.DEV) {
    console.log(`[Analytics] Event: ${event}`, properties || '');
  }
}

/**
 * Track page view manually (for SPAs with custom routing)
 */
export function trackPageView(path?: string): void {
  if (import.meta.env.DEV) {
    console.log('[Analytics] Page view:', path || window.location.pathname);
  }
}

/**
 * Set user properties without tracking an event
 */
export function setUserProperties(_properties: Record<string, unknown>): void {
  // No-op in stub implementation
}

/**
 * Set properties that should only be set once (e.g., signup date)
 */
export function setUserPropertiesOnce(_properties: Record<string, unknown>): void {
  // No-op in stub implementation
}

/**
 * Increment a numeric property
 */
export function incrementUserProperty(_property: string, _value = 1): void {
  // No-op in stub implementation
}

/**
 * Start timing an event (returns a function to stop timing)
 */
export function startTiming(eventName: string): () => void {
  const startTime = Date.now();
  
  return () => {
    const duration = Date.now() - startTime;
    trackEvent(eventName, { duration_ms: duration });
  };
}

/**
 * Track a funnel step
 */
export function trackFunnelStep(
  funnelName: string,
  step: number,
  stepName: string,
  properties?: Record<string, unknown>
): void {
  trackEvent(`${funnelName}_step_${step}`, {
    funnel: funnelName,
    step,
    step_name: stepName,
    ...properties,
  });
}

/**
 * Feature flags - returns false since PostHog is not installed
 */
export function isFeatureEnabled(_flagKey: string): boolean {
  return false;
}

/**
 * Get feature flag value - returns null since PostHog is not installed
 */
export function getFeatureFlag(_flagKey: string): unknown {
  return null;
}

/**
 * React hook for analytics (optional wrapper)
 */
export function useAnalytics() {
  return {
    track: trackEvent,
    identify: identifyUser,
    reset: resetAnalyticsUser,
    pageView: trackPageView,
    setProperties: setUserProperties,
    isFeatureEnabled,
  };
}

